/****************************************************
 * Module Name:  kapanLossTimeWiseReport.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Button, FormControlLabel, FormGroup, Switch, Tooltip, Typography } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LoadingButton } from "@mui/lab";
import CommonSnackbar from "../common/commonSnackBar";
import Constants from "../../utility/constants";
import { v4 as uuidv4 } from "uuid";
import KapanlosslistModal from "./kapanlosslistModal";
import RemarkModal from "../common/remarkModal";
import NoPermission from "../common/noPermission";
import dayjs from "dayjs";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PageTitle from "../common/pageTitle";

function TotalStockComponent({ data }) {
    const makeData = (data) => {
        if (!Array.isArray(data)) return;
        let totaldata = data.length;
        let weight = 0;
        let amount = 0;
        let packets = 0;
        data.forEach((item) => {
            weight += item.totalweight;
            amount += item.totalloss;
            packets += item.totalpackets;
        });


        return [
            totaldata,
            weight,
            amount,
            packets
        ];
    };

    const [
        totaldata,
        weight,
        amount,
        packets
    ] = makeData(data);

    return (
        <TableContainer component={Paper} sx={{ m: "15px 15px 0 0" }}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
                <TableHead>
                    <TableRow
                        sx={{
                            "& .css-12q85cb-MuiTableCell-root": {
                                fontWeight: 600,
                                backgroundColor: "black",
                                color: "white",
                            },
                        }}
                    >
                        <TableCell align="center">Total Data</TableCell>
                        <TableCell align="center">Total Weight</TableCell>
                        <TableCell align="center">Total Loss</TableCell>
                        <TableCell align="center">Total Packets</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell align="center">{totaldata}</TableCell>
                        <TableCell align="center">
                            {weight ? weight.toFixed(2) : 0}
                        </TableCell>
                        <TableCell align="center">
                            {amount ? amount.toFixed(2) : 0}
                        </TableCell>
                        <TableCell align="center">
                            {packets}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}


const KapanLossTimeWiseReport = () => {

    const [sourceData, setSourceData] = useState([]);

    const [
        departmentTransactionListListLoader,
        setDepartmentTransactionListListLoader,
    ] = useState(false);
    const [data, setData] = useState([]);
    const [fetchData, setFetchData] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [kapanId, setKapanId] = useState("");
    const [showKapanlossDetailModal, setShowKapanLossDetailModal] = useState(false);
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [access, setAccess] = useState(false);
    const [showRemarkModal, setShowRemarkModal] = useState(false);
    const [showInlineRemark, setShowInlineRemark] = useState(true);

    let date = new Date(); //Current Date and Time

    useEffect(() => {
        PageTitle("MANUFACTURING_REPORT_LOSS_KAPANLOSSTIMEWISE");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MANUFACTURING_REPORT_LOSS_KAPANLOSSTIMEWISE
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        } else {
            setFromDate(new Date(date.setDate(date.getDate() - 1)));
            setToDate(new Date());
            setFetchData(true)
        }
    }

    useEffect(() => {
        if (fetchData) {
            if (fromDate && toDate) {
                handleSearch(fromDate, toDate);
            }
        }
    }, [fetchData, toDate, fromDate]);

    const handleSearch = (fromDate, toDate) => {
        let data = {
            from:
                dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z",
            to: dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z",
        };
        setDepartmentTransactionListListLoader(true);
        ReportProxy.GetLossByKapanTimeWise(data, (response) => {
            if (response) {
                let data = response
                for (let i = 0; i < data.length; i++) {
                    data[i].id = uuidv4();
                }
                setSourceData(data);
                setDepartmentTransactionListListLoader(false);
                setFetchData(false);
            } else {
                setDepartmentTransactionListListLoader(false);
                setErrorSnackbar(true);
            }
        });
    };

    const handleClose = () => {
        setShowKapanLossDetailModal(false);
        setShowRemarkModal(false)
    };

    const handleClick = (params) => {
        setKapanId(params.row.kapanid)
        setShowKapanLossDetailModal(true)
    }

    const handleRemarkModal = (params) => {
        setData(params)
        setShowRemarkModal(true)
    }


    const columnName = [
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        {
            field: "totalweight",
            headerName: "TOTAL WEIGHT",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "totalloss",
            headerName: "TOTAL LOSS",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "totaluser",
            headerName: "TOTAL USER",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "totalpackets",
            headerName: "TOTAL PACKETS",
            headerAlign: "center",
            align: "center",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            flex: 2,
            minWidth: 350,
            renderCell: (params) => (
                params.row.remarks === null || params.row.lossremarks === "" ? '' :
                    showInlineRemark ?
                        <Box sx={{ minHeight: "auto" }}>

                            <table style={{ border: '1pt solid black' }}>
                                <tr style={{ border: '1pt solid black' }}>
                                    <th style={{ border: '1pt solid black' }}>Loss Category</th>
                                    <th style={{ border: '1pt solid black' }}>Loss Remarks</th>
                                </tr>
                                {
                                    Utility.GetRemark(params.row.remarks).map((iRow) =>
                                        <tr key={iRow.id} style={{ border: '1pt solid black' }}>
                                            <td style={{ border: '1pt solid black' }}>{iRow.category}</td>
                                            <td style={{ border: '1pt solid black' }}>{iRow.remark}</td>
                                        </tr>
                                    )
                                }
                            </table>
                        </Box>
                        :
                        <Button
                            sx={{ width: 220 }}
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleRemarkModal(params.row.remarks)
                            }}
                        >
                            Show Loss Remark
                        </Button>
            ),
        },
    ];
    const rows = sourceData;
    const columns = columnName;
    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {showKapanlossDetailModal ? (
                <KapanlosslistModal
                    show={showKapanlossDetailModal}
                    kapanId={kapanId}
                    fromdate={fromDate}
                    todate={toDate}
                    close={handleClose}
                    time={true}
                />
            ) : null}
            {showRemarkModal ? (
                <RemarkModal
                    show={showRemarkModal}
                    sourceData={data}
                    handleClose={handleClose}
                />
            ) : null}
            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                mt: 3,
                                mb: 1,
                                gap: "15px",
                                flexWrap: "wrap",
                                alignItems: "center",
                            }}
                        >
                            <Box display={"flex"} gap="15px" ml={1}>
                                <Typography mt={2}>From</Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        className="picker"
                                        ampm={true}
                                        renderInput={(props) => (
                                            <TextField
                                                fullWidth
                                                {...props}
                                                inputProps={{ ...props.inputProps, readOnly: true }}
                                            />
                                        )}
                                        value={fromDate}
                                        inputFormat="DD/MM/YYYY"
                                        onChange={(newValue) => {
                                            // Get the current time
                                            const now = dayjs();

                                            // Set startOfMonth to the first day of the selected month, but with the current system time
                                            const fromDate = dayjs(newValue)
                                                .hour(now.hour())
                                                .minute(now.minute())
                                                .second(now.second())
                                                .millisecond(now.millisecond())
                                                .toDate();

                                            setFromDate(fromDate);
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <Box display={"flex"} gap="15px">
                                {" "}
                                <Typography mt={2}>To</Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        className="picker"
                                        ampm={true}
                                        renderInput={(props) => (
                                            <TextField
                                                fullWidth
                                                {...props}
                                                inputProps={{ ...props.inputProps, readOnly: true }}
                                            />
                                        )}
                                        error={false}
                                        value={toDate}
                                        inputFormat="DD/MM/YYYY"
                                        onChange={(newValue) => {
                                            // Get the current time
                                            const now = dayjs();

                                            // Set startOfMonth to the first day of the selected month, but with the current system time
                                            const toDate = dayjs(newValue)
                                                .hour(now.hour())
                                                .minute(now.minute())
                                                .second(now.second())
                                                .millisecond(now.millisecond())
                                                .toDate();

                                            setToDate(toDate);
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                <LoadingButton
                                    loading={departmentTransactionListListLoader}
                                    loadingPosition="start"
                                    variant="contained"
                                    onClick={() => handleSearch(fromDate, toDate)}
                                    startIcon={<SearchOutlinedIcon size="large" />}
                                >
                                    Search
                                </LoadingButton>
                            </Box>
                            <FormGroup sx={{ mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            disabled={departmentTransactionListListLoader}
                                            checked={showInlineRemark}
                                            onChange={() => setShowInlineRemark(!showInlineRemark)}
                                        />
                                    }
                                    label="Inline Loss Remark"
                                />
                            </FormGroup>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "80vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                slots={{ toolbar: GridToolbar }}
                                rows={rows}
                                loading={departmentTransactionListListLoader}
                                onRowClick={handleClick}
                                columns={columns}
                                getRowId={(row) => row.id}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    sorting: {
                                        sortModel: [{ field: "id", sort: "asc" }],
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box
                                style={{
                                    position: "absolute",
                                    top: 5,
                                    right: "270px",
                                }}
                            >
                                {departmentTransactionListListLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() =>
                                                handleSearch(fromDate, toDate)
                                            }
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                        <Box height={100}>
                            <TotalStockComponent data={sourceData} />
                        </Box>
                    </Box>
                )
            }
        </div>
    );
};

export default KapanLossTimeWiseReport;
